import { inject, Injectable } from '@angular/core'
import { BehaviorSubject, from, Observable, of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { CepService } from './cep.service'

export interface City {
	nome: string
	codigo_uf: number
	latitude: number
	longitude: number
}

export const UF_CODE_MAP: Record<number, string> = {
	11: 'RO',
	12: 'AC',
	13: 'AM',
	14: 'RR',
	15: 'PA',
	16: 'AP',
	17: 'TO',
	21: 'MA',
	22: 'PI',
	23: 'CE',
	24: 'RN',
	25: 'PB',
	26: 'PE',
	27: 'AL',
	28: 'SE',
	29: 'BA',
	31: 'MG',
	32: 'ES',
	33: 'RJ',
	35: 'SP',
	41: 'PR',
	42: 'SC',
	43: 'RS',
	50: 'MS',
	51: 'MT',
	52: 'GO',
	53: 'DF',
}

@Injectable({ providedIn: 'root' })
export class CityService {
	private citiesSubject = new BehaviorSubject<City[]>([])
	private cepService = inject(CepService)
	cities$ = this.citiesSubject.asObservable()

	constructor() {
		this.loadCities()
	}

	private loadCities() {
		from(fetch('https://tagmepub.blob.core.windows.net/pubfiles/municipios.json').then(res => res.json()))
			.pipe(catchError(() => of([])))
			.subscribe((data: City[]) => this.citiesSubject.next(data))
	}

	getUfCodeMap() {
		return UF_CODE_MAP
	}

	filterCities(cities: City[], searchText: string): Observable<City[]> {
		const normalize = (value: string) =>
			value
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '')
				.toLowerCase()
				.trim()

		const normalizedSearch = normalize(searchText)
		if (normalizedSearch.length < 3) return of([])

		// CEP
		if (/^\d+$/.test(searchText)) {
			return this.cepService.findCep(searchText).pipe(
				map(res => {
					if (!res || res.error) return []

					// tenta encontrar a cidade correspondente no cache (municipios.json)
					const found = cities.find(
						c =>
							normalize(c.nome) === normalize(res.city) &&
							UF_CODE_MAP[c.codigo_uf] === res.state
					)

					if (found) return [found]

					// fallback se não achar nos dados locais
					const ufCode = Object.keys(UF_CODE_MAP).find(key => UF_CODE_MAP[+key] === res.state)
					return [
						{
							nome: res.city,
							codigo_uf: ufCode ? +ufCode : 0,
							latitude: 0,
							longitude: 0,
						},
					]
				}),
				catchError(() => of([]))
			)
		}

		// Texto
		return of(
			cities.filter(
				c =>
					normalize(c.nome).includes(normalizedSearch) ||
					normalize(UF_CODE_MAP[c.codigo_uf] || '').includes(normalizedSearch)
			)
		)
	}
}
